import type { LinearRGB } from '../../../core/color';
import type { Landmark } from '../../../data/types';
import { PATTERN } from '../../../render/patterns/patternIds';
import { SURFACE } from '../../mesh/attributes';
import type { MeshBuilder } from '../../mesh/meshBuilder';
import { horizontalCap, pyramidOverRing, ringPoints } from '../solids';
import { buildVessel } from './churchVessel';
import { buildConventRange } from './conventRange';
import { buildConventSoil } from './conventSoil';
import type { LandmarkContext } from './landmarkFeature';
import { centredWindow, rectWall } from './masonry';
import {
  sintMariaChapel,
  sintMariaFloors,
  sintMariaFrame,
  sintMariaRangeFrame,
} from './sintMariaPlan';
import reading from './sintMariaPlan.json';
import { paintedChapelStyles, type PaintedChapelColors } from './paintedChapels';
import { meshFrameOf } from './planFrame';
import { NO_TRACERY, type TracerySink } from './tracery/tracerySink';

/** Sint Maria before the 1541 fire: separate wings around a long open court. */
export function buildSintMaria(
  builder: MeshBuilder,
  landmark: Landmark,
  colors: PaintedChapelColors & {
    tile: LinearRGB;
    ground: LinearRGB;
    garden: LinearRGB;
  },
  ids: { readonly edgeId: number; readonly seed: number },
  tracery: TracerySink = NO_TRACERY,
  context: Pick<LandmarkContext, 'elevation' | 'elevationAt'> = {}
): void {
  const plan = sintMariaFrame(landmark),
    frame = meshFrameOf(plan),
    styles = paintedChapelStyles(colors, ids, tracery),
    brick = { ...styles, wall: { ...styles.wall, surface: SURFACE.brick } },
    chapel = sintMariaChapel();
  // The northern body is tile-coloured in the painting; its exact chapel elevation is provisional.
  buildVessel(
    builder,
    {
      ...brick,
      roof: {
        ...brick.roof,
        color: colors.tile,
        surface: SURFACE.roofTile,
        pattern: PATTERN.roofTileHollePan,
      },
    },
    frame,
    chapel,
    {
      longWindows: { sill: 3, top: chapel.eaveY - 1.2 },
      westWindow: true,
      bay: 6.2,
    }
  );
  const bell = reading.belfry,
    ring = ringPoints([bell.u, bell.v], bell.radius, 8, Math.PI / 8);
  ring.forEach((from, i) => {
    const to = ring[(i + 1) % ring.length]!,
      width = Math.hypot(to[0] - from[0], to[1] - from[1]);
    rectWall(
      builder,
      brick,
      frame,
      [from, to],
      [bell.foot, bell.eaves],
      centredWindow(width, 0.45, bell.eaves - bell.foot - 0.3, 0.5),
      bell.foot
    );
  });
  horizontalCap(builder, brick.wall, frame, ring, bell.foot, false);
  pyramidOverRing(builder, brick, frame, ring, bell.eaves, { plan: [bell.u, bell.v], y: bell.tip });
  for (const part of reading.ranges)
    buildConventRange(
      builder,
      meshFrameOf(sintMariaRangeFrame(plan, part)),
      part.roof === 'tile'
        ? {
            ...brick,
            roof: {
              ...brick.roof,
              color: colors.tile,
              surface: SURFACE.roofTile,
              pattern: PATTERN.roofTileHollePan,
            },
          }
        : brick,
      part
    );
  const base = context.elevation ?? 0,
    terrain = context.elevationAt ?? (() => base);
  for (const floor of sintMariaFloors(landmark))
    buildConventSoil(
      builder,
      { ...brick.wall, color: floor.garden ? colors.garden : colors.ground },
      floor.ring,
      terrain,
      base,
      floor.height,
      floor.garden
    );
}
